import { mapState } from 'vuex'

export default {
  mounted() {
    if (!this.multiContext.charity && !this.isAdminLoginRoute(this.$route)) {
      return
    }

    this.updateLaunchDarklyContexts()
  },

  computed: {
    ...mapState({
      activeCharityInfo: (state) => state.charities.activeCharityInfo,
      adminInfo: (state) => state.auth.adminInfo,
      accessToken: (state) => state.auth.accessToken,
    }),

    userContext() {
      const baseContext = {
        // LaunchDarkly requires a `key` in order to evaluate/track flags reliably.
        // When user info isn't available yet (login/reset flows), fall back to an anonymous key.
        key: 'anonymous',
        anonymous: !this.accessToken || !this.adminInfo,
      }

      // If user is not logged in, return base context for user
      if (!this.accessToken || !this.adminInfo) {
        return baseContext
      }

      const userName = `${this.adminInfo.first_name} ${this.adminInfo.last_name}`

      // Else return user_id as key (required) and user details
      return {
        ...baseContext,
        key: this.adminInfo.id,
        user_id: this.adminInfo.id,
        email: this.adminInfo.email,
        first_name: this.adminInfo.first_name,
        last_name: this.adminInfo.last_name,
        name: userName,
        ...(this.adminInfo.details ? { phone_number: this.adminInfo.details.phone_number } : {}),
      }
    },

    charityContext() {
      // If charity info is not set yet return null (required)
      if (!this.activeCharityInfo || !this.activeCharityInfo.charity_id) {
        return null
      }

      return {
        key: this.activeCharityInfo.charity_id,
        charity_id: this.activeCharityInfo.charity_id,
        charity_name: this.activeCharityInfo.charity_name,
        name: this.activeCharityInfo.charity_name,
      }
    },

    multiContext() {
      return {
        kind: 'multi',
        user: this.userContext,
        charity: this.charityContext,
      }
    },
  },

  methods: {
    isAdminLoginRoute(route) {
      const currentRoute = route || {}
      return Boolean(
        currentRoute.name === 'adminLogin' ||
          currentRoute.path === '/admin/login' ||
          (Array.isArray(currentRoute.matched) &&
            currentRoute.matched.some((record) => record && record.name === 'adminLogin'))
      )
    },

    async updateLaunchDarklyContexts() {
      // Use multi-context when charity is loaded; otherwise use user-only context
      // (anonymous or identified). Mount and $route only trigger LD init without
      // charity on the admin login route, so flags like useNpDashLogin still work
      // before charity exists. After charity is set, identify() runs with full context.
      const contextToUse = this.multiContext.charity ? this.multiContext : this.userContext

      if (!this.$launchDarkly.isInitialized()) {
        this.$launchDarkly.initialize(contextToUse)
      } else {
        const ldClient = await this.$launchDarkly.getClient()
        await ldClient.identify(contextToUse)
      }

      const client = await this.$launchDarkly.getClient()
      await this.$store.dispatch('featureFlags/bindLaunchDarklyClient', client)
    },
  },

  watch: {
    $route: {
      handler: function (newRoute) {
        if (this.multiContext.charity || this.isAdminLoginRoute(newRoute)) {
          this.updateLaunchDarklyContexts()
        }
      },
      deep: true,
    },
    multiContext: {
      handler: async function (newMultiContext) {
        // A charity is necessary before identification can start
        if (!newMultiContext.charity) {
          return
        }

        this.updateLaunchDarklyContexts()
      },
      deep: true,
    },
  },
}
